import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { constructNow, format } from 'date-fns';
import { indexBy, prop } from 'ramda';

import ooeConstants from '../constants';
import { logDatadogError } from '../services/datadog';
import { dashboardErrorMessages } from '../util/customerErrorMessages';
import { DashboardOrder } from './schemas';
import {
  cancelOrder,
  getAdditionalPastOrders,
  getDataForExcelExtraction,
  getOrdersForSpecificDays,
  getRecentAndUpcomingOrders,
  lookupCustomerDetails,
  lookupOrderDetails,
  lookupPosOrderDetails,
  resendPaymentEmail,
} from './thunks';

export type State = {
  loading: {
    orders: boolean;
    customerDetails: boolean;
    cancelOrder: boolean;
    resendEmail: boolean;
    orderDetails: boolean;
    excelExtraction: boolean;
    loadMorePastOrders: boolean;
  };
  error: {
    orders: Nullable<string>;
    customerDetails: Nullable<string>;
    orderDetails: Nullable<string>;
    cancelOrder: Nullable<string>;
    resendEmail: Nullable<string>;
    excelExtraction: Nullable<string>;
    loadMorePastOrders: Nullable<string>;
  };
  orderToViewId: string;
  orders: Record<string, DashboardOrder>;
  startDate: Nullable<Date>;
  endDate: Nullable<Date>;
  exportExcelOrders: Array<DashboardOrder>;
  allowExcelDownload: boolean;
  errorOccured: boolean;
  loadingExcelAction: boolean;
  specificDays: {
    startDate?: Date;
    endDate?: Date;
  };
  emailSent: boolean;
};

const initialState: State = {
  loading: {
    orders: false,
    customerDetails: false,
    cancelOrder: false,
    resendEmail: false,
    orderDetails: false,
    excelExtraction: false,
    loadMorePastOrders: false,
  },
  error: {
    orders: null,
    customerDetails: null,
    orderDetails: null,
    cancelOrder: null,
    resendEmail: null,
    excelExtraction: null,
    loadMorePastOrders: null,
  },
  orderToViewId: '',
  orders: {},
  startDate: null,
  endDate: null,
  exportExcelOrders: [],
  allowExcelDownload: false,
  errorOccured: false,
  loadingExcelAction: false,
  specificDays: {},
  emailSent: false,
};

const slice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    loadingOrders: (state) => {
      state.loading.orders = true;
      state.error.orders = null;
    },
    downloadExcel: (state) => {
      state.loadingExcelAction = false;
      state.allowExcelDownload = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrdersForSpecificDays.pending, (state, action) => {
        const { startDate, endDate } = action.meta.arg;
        state.loading.orders = true;
        state.error.orders = null;
        state.exportExcelOrders = [];
        state.errorOccured = false;
        state.specificDays.startDate = startDate;
        state.specificDays.endDate = endDate;
      })
      .addCase(getOrdersForSpecificDays.fulfilled, (state, action) => {
        const { orders } = action.payload;
        state.orders = getCateringOrdersById(orders);
        state.loading.orders = false;
        state.error.orders = null;
      })
      .addCase(getOrdersForSpecificDays.rejected, (state, action) => {
        const error = action.error;
        logDatadogError('Get Orders For Specific Days Error', { dashboard: state }, error);
        state.loading.orders = false;
        state.error.orders = error.message ?? null;
      })
      .addCase(getRecentAndUpcomingOrders.pending, (state) => {
        state.loading.orders = true;
        state.error.orders = null;
        state.exportExcelOrders = [];
        state.errorOccured = false;
        state.specificDays = {};
      })
      .addCase(getRecentAndUpcomingOrders.fulfilled, (state, action) => {
        state.loading.orders = false;
        state.error.orders = null;

        const { orders, cutoffDate } = action.payload;
        state.orders = getCateringOrdersById(orders);

        const { specificDays } = state;
        if (specificDays?.startDate) {
          state.specificDays.startDate = cutoffDate;
        } else {
          state.endDate = cutoffDate;
        }
      })
      .addCase(getRecentAndUpcomingOrders.rejected, (state, action) => {
        const error = action.error;
        logDatadogError('Get Recent And Upcoming Orders Error', { dashboard: state }, error);
        state.loading.orders = false;
        state.error.orders = error.message ?? null;
      })
      .addCase(getAdditionalPastOrders.pending, (state) => {
        state.loading.loadMorePastOrders = true;
        state.error.loadMorePastOrders = null;
      })
      .addCase(getAdditionalPastOrders.fulfilled, (state, action) => {
        const { orders } = action.payload;
        const cateringOrders = getCateringOrdersById(orders);
        state.orders = {
          ...state.orders,
          ...cateringOrders,
        };
        state.loading.loadMorePastOrders = false;
        state.error.loadMorePastOrders = null;

        const { startDate } = action.meta.arg;
        const { specificDays } = state;
        if (specificDays?.startDate) {
          state.specificDays.startDate = startDate;
        } else {
          state.endDate = startDate;
        }
      })
      .addCase(getAdditionalPastOrders.rejected, (state, action) => {
        const error = action.error;
        logDatadogError('Get Additional Past Orders Error', { dashboard: state }, error);
        state.loading.loadMorePastOrders = false;
        state.error.loadMorePastOrders = error.message ?? null;
      })
      .addCase(cancelOrder.pending, (state, action) => {
        const { orderId } = action.meta.arg;
        state.loading.cancelOrder = true;
        state.error.cancelOrder = null;
        state.orderToViewId = orderId;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading.cancelOrder = false;
        state.error.cancelOrder = null;

        const { orderId } = action.meta.arg;
        const order = state.orders[orderId];
        if (!order) {
          return;
        }

        state.orders[orderId].status = 'Cancelled';
        state.orders[orderId].displayStatus = { statusName: 'Cancelled', statusIcon: 'error' };
        state.orders[orderId].timestamps = {
          ...state.orders[orderId].timestamps,
          cancel: format(constructNow(new Date()), ooeConstants.DATE_TIME_FORMAT.dateTime),
        };
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        logDatadogError('Cancel Order Error', { dashboard: state }, action.error);
        state.loading.cancelOrder = false;
        state.error.cancelOrder = dashboardErrorMessages.CANCEL_ERROR;
      })
      .addCase(getDataForExcelExtraction.pending, (state) => {
        state.loading.excelExtraction = true;
        state.error.excelExtraction = null;
        state.exportExcelOrders = [];
        state.errorOccured = false;
        state.loadingExcelAction = true;
      })
      .addCase(getDataForExcelExtraction.fulfilled, (state, action) => {
        const { orderWithDetails } = action.payload;
        state.exportExcelOrders.push(orderWithDetails);
        state.loading.excelExtraction = false;
        state.error.excelExtraction = null;
        state.allowExcelDownload = true;
      })
      .addCase(getDataForExcelExtraction.rejected, (state, action) => {
        logDatadogError('Get Data For Excel Extraction Error', { dashboard: state }, action.error);
        state.loading.excelExtraction = false;
        state.error.excelExtraction = dashboardErrorMessages.GET_EXCEL_DATA_ERROR;
        state.allowExcelDownload = false;
        state.errorOccured = true;
        state.loadingExcelAction = false;
      })
      .addCase(lookupCustomerDetails.pending, (state, action) => {
        const { orderId } = action.meta.arg;
        state.loading.customerDetails = true;
        state.error.customerDetails = null;
        state.orderToViewId = orderId;
      })
      .addCase(lookupCustomerDetails.fulfilled, (state, action) => {
        const { customerDetails } = action.payload;
        const existingOrder = state.orders[state.orderToViewId];
        const phone = customerDetails.phoneNumber || existingOrder?.phone || existingOrder?.user?.phoneNumber;
        const email = customerDetails.emailAddress;

        state.orders[state.orderToViewId] = {
          ...existingOrder,
          email,
          phone,
          givenName: customerDetails.givenName,
          familyName: customerDetails.familyName,
          accountType: customerDetails.accountType,
        };

        state.loading.customerDetails = false;
        state.error.customerDetails = null;
        state.error.cancelOrder = null;
        state.error.resendEmail = null;
        state.error.orderDetails = null;

        if (!email && !phone) {
          state.error.customerDetails = dashboardErrorMessages.DETAILS_ERROR;
        } else if (!phone) {
          state.error.customerDetails = dashboardErrorMessages.NO_PHONE;
        } else if (!email) {
          state.error.customerDetails = dashboardErrorMessages.NO_EMAIL;
        }
      })
      .addCase(lookupCustomerDetails.rejected, (state, action) => {
        logDatadogError('Lookup Customer Details Error', { dashboard: state }, action.error);
        state.loading.customerDetails = false;
        state.error.customerDetails = dashboardErrorMessages.DETAILS_ERROR;
      })
      .addCase(resendPaymentEmail.pending, (state, action) => {
        const { orderId } = action.meta.arg;
        state.loading.resendEmail = true;
        state.error.resendEmail = null;
        state.orderToViewId = orderId;
      })
      .addCase(resendPaymentEmail.fulfilled, (state) => {
        state.loading.resendEmail = false;
        state.error.resendEmail = null;
        state.emailSent = true;
        state.orders[state.orderToViewId].timestamps = {
          ...state.orders[state.orderToViewId].timestamps,
          paymentPending: format(constructNow(new Date()), ooeConstants.DATE_TIME_FORMAT.dateTime),
        };
      })
      .addCase(resendPaymentEmail.rejected, (state, action) => {
        logDatadogError('Resend Payment Email Error', { dashboard: state }, action.error);
        state.loading.resendEmail = false;
        state.error.resendEmail = dashboardErrorMessages.RESEND_EMAIL_ERROR;
        state.emailSent = false;
      })
      .addMatcher(isAnyOf(lookupPosOrderDetails.pending, lookupOrderDetails.pending), (state, action) => {
        const { orderId } = action.meta.arg;
        state.loading.orderDetails = true;
        state.error.orderDetails = null;
        state.orderToViewId = orderId;
      })
      .addMatcher(isAnyOf(lookupPosOrderDetails.fulfilled, lookupOrderDetails.fulfilled), (state, action) => {
        const { order } = action.payload;
        state.emailSent = false;
        state.loading.orderDetails = false;
        state.error.orderDetails = null;
        state.error.cancelOrder = null;
        state.error.resendEmail = null;

        const existingOrder = state.orders[state.orderToViewId];
        const mergedOrder = {
          ...existingOrder,
          ...order,
        };
        const phone = mergedOrder.phone || order.user?.phoneNumber;
        if (phone) {
          mergedOrder.phone = phone;
        }
        state.orders[state.orderToViewId] = mergedOrder;

        if (phone && state.error.customerDetails === dashboardErrorMessages.NO_PHONE) {
          state.error.customerDetails = null;
        } else if (phone && state.error.customerDetails === dashboardErrorMessages.DETAILS_ERROR) {
          state.error.customerDetails = !mergedOrder.email ? dashboardErrorMessages.NO_EMAIL : null;
        }
      })
      .addMatcher(isAnyOf(lookupPosOrderDetails.rejected, lookupOrderDetails.rejected), (state, action) => {
        logDatadogError('Lookup Order Details Error', { dashboard: state }, action.error);
        state.emailSent = false;
        state.loading.orderDetails = false;
        state.error.orderDetails = dashboardErrorMessages.ORDER_ERROR;
      });
  },
});

export const actions = slice.actions;
export default slice.reducer;

const getCateringOrdersById = (orders: Array<DashboardOrder>) => {
  const filteredOrders = orders.filter(
    (order) =>
      order.destination !== 'On demand' &&
      order.destination !== 'Curbside' &&
      order.destination !== 'Cfa delivery' &&
      order.destination !== 'Unspecified',
  );
  const indexedOrders = indexBy(prop('id'), filteredOrders);
  return indexedOrders;
};
